import type { ReactNode } from "react";
import { cn } from "../cn";

export interface FilterFieldProps {
  /** Caption shown above the control. */
  readonly label: string;
  readonly children: ReactNode;
  readonly className?: string;
}

/**
 * A labelled control in a filter bar: a small caption stacked above a date
 * picker, combobox or input.
 *
 * The caption is hidden from assistive technology, because the control it
 * labels carries its own `aria-label` and announcing the name twice helps
 * nobody. It stays visible because a bare date field or combobox does not say
 * what it filters on, so callers are expected to give the control an
 * `aria-label` matching the caption.
 */
export function FilterField({
  label,
  children,
  className,
}: FilterFieldProps): ReactNode {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <span
        className="text-xs font-medium text-muted-foreground"
        aria-hidden="true"
      >
        {label}
      </span>
      {children}
    </div>
  );
}
